import React, { useMemo, useState } from 'react';
import { TestResult } from '../types';
import { analyzeLSI } from '../services/geminiService';

interface LsiTabProps {
  results: TestResult | null;
}

const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center p-4">
    <svg className="animate-spin h-5 w-5 text-cyan-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
    <span className="ml-3">Analyzing...</span>
  </div>
);

export const LsiTab: React.FC<LsiTabProps> = ({ results }) => {
    const [aiAnalysis, setAiAnalysis] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const lsiData = useMemo(() => {
        if (!results || results.ph == null || !results.temp || results.ch == null || results.ta == null) return null;
        const { temp, ch, ta, cya, ph, tds } = results;

        type FactorTable = ReadonlyArray<readonly [number, number]>;
        const getFactor = (value: number, table: FactorTable): number => {
            for (const [limit, factor] of table) { if (value <= limit) return factor; }
            const last = table[table.length - 1];
            return last !== undefined ? last[1] : 0;
        };
        // Taylor K-2006 standard lookup tables for LSI factors
        const tempFactorTable: FactorTable = [[32,0.0],[38,0.1],[46,0.2],[53,0.3],[60,0.4],[66,0.5],[76,0.6],[84,0.7],[94,0.8],[105,0.9]];
        const chFactorTable: FactorTable   = [[25,1.0],[50,1.3],[100,1.6],[200,1.9],[400,2.2],[800,2.5],[1000,2.6]];
        const taFactorTable: FactorTable   = [[25,1.4],[50,1.7],[100,2.0],[200,2.3],[400,2.6],[800,2.9],[1000,3.0]];

        const getTdsFactor = (tdsValue: number): number => {
            if (tdsValue < 1000) return 12.1;
            if (tdsValue <= 2000) return 12.2;
            if (tdsValue <= 3000) return 12.3;
            return 12.4;
        };

        const TF = getFactor(temp, tempFactorTable);
        const CF_Ca = getFactor(ch, chFactorTable);
        // CYA correction: subtract ~1/3 of CYA from TA for LSI (cyanurate alkalinity)
        const correctedAlk = Math.max(1, ta - (cya != null && cya > 0 ? cya * 0.33 : 0));
        const AF = getFactor(correctedAlk, taFactorTable);
        const CF_TDS = getTdsFactor(tds || 500);

        const lsi = ph + TF + CF_Ca + AF - CF_TDS;

        let text = 'Balanced', color = 'text-green-400';
        if (lsi < -0.3) { text = 'Corrosive'; color = 'text-red-400'; }
        else if (lsi > 0.3) { text = 'Scaling'; color = 'text-orange-400'; }

        return {
            lsi, text, color,
            TF, CF_Ca, AF, CF_TDS, correctedAlk,
            ph, temp, ch, ta,
            cya: cya ?? 0,
            tds: tds ?? 500,
        };
    }, [results]);

    const handleAnalyze = async () => {
        if (!results || !lsiData) return;
        setIsLoading(true);
        setError('');
        setAiAnalysis('');
        try {
            const analysis = await analyzeLSI(lsiData);
            setAiAnalysis(analysis);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    };

    if (!results || !lsiData) {
        return (
            <div className="text-center text-slate-400 py-8 bg-slate-800 rounded-lg">
                <p className="text-lg">No test results available.</p>
                <p>Complete a test on the "New Test" tab to calculate Water Balance.</p>
            </div>
        );
    }

  return (
    <div className="printable-content bg-slate-800 p-4 rounded-lg shadow-md animate-fade-in">
      <h2 className="text-xl font-semibold text-white text-center">Water Balance Calculator (LSI)</h2>
      <p className="text-center text-sm text-slate-400 mt-1 mb-4">Calculates using values from your latest test.</p>

      <div className="mt-6 bg-slate-900/50 p-4 rounded-md text-center">
        <p className="text-sm text-cyan-400">Langelier Saturation Index (LSI)</p>
        <p className={`text-4xl font-bold my-1 ${lsiData.color}`}>{lsiData.lsi.toFixed(2)}</p>
        <p className={`font-semibold ${lsiData.color}`}>{lsiData.text}</p>
      </div>
      <div className="mt-4 text-xs text-slate-500 grid grid-cols-3 text-center">
        <div><strong className="text-red-400">&lt; -0.3</strong><br/>Corrosive</div>
        <div><strong className="text-green-400">-0.3 to +0.3</strong><br/>Balanced</div>
        <div><strong className="text-orange-400">&gt; +0.3</strong><br/>Scaling</div>
      </div>

      <div className="mt-6 border-t border-slate-700 pt-4">
         <div className="flex flex-wrap gap-2 justify-center">
            <button onClick={handleAnalyze} disabled={isLoading} className="no-print flex-grow bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 disabled:bg-slate-600 disabled:cursor-not-allowed">
                Analyze with AI
            </button>
            <button onClick={() => window.print()} className="no-print bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><polyline points="6 9 6 2 18 2 18 9"></polyline><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path><rect x="6" y="14" width="12" height="8"></rect></svg>
                Print
            </button>
        </div>
        {isLoading && <LoadingSpinner />}
        {error && <div className="mt-4 bg-red-900/50 text-red-300 p-3 rounded-md">{error}</div>}
        {aiAnalysis && (
            <div className="mt-4 bg-slate-900/50 p-4 rounded-lg space-y-2 whitespace-pre-wrap font-sans">
                 <h4 className="font-bold text-lg text-cyan-400">AI Analysis:</h4>
                 <p className="text-slate-300">{aiAnalysis}</p>
            </div>
        )}
      </div>
    </div>
  );
};
